import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import Header from '../../../components/util/header';
import { Group, Loader, Table } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import PaginationComponent from '../../../components/pagination';
import { fetchAuditLogs, IAuditLog } from '../../../../libraries/helpdesk/audit-logs';
import ShowAuditLog from '../../components/show-auditlog';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../../utils/date';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import './styles.scss';

const HelpdeskAuditLogs = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const controller = useRef(new AbortController()).current;
  const [list, setList] = useState<IAuditLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const [openDetails, setOpenDetails] = useState<boolean>(true);
  const [, setActiveRow] = useState<number>();
  const [selectedLog, setSelectedLog] = useState<IAuditLog>();
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadAuditLogs();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadAuditLogs = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchAuditLogs(
      controller,
      offset,
      limit,
      true,
      filterBarData?.eid,
      formatDateToISO(filterBarData?.beginDateTime ?? undefined),
      formatDateToISO(filterBarData?.endDateTime ?? undefined)
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const showAuditLogDetails = (index: number) => {
    if (list) {
      setActiveRow(index);
      setSelectedLog(list?.[index]);
      setOpenDetails(true);
    }
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="audit-logs">
      {openDetails && selectedLog && (
        <ShowAuditLog log={selectedLog} openDetails={openDetails} hideBtn={() => setOpenDetails(false)} lang={lang} />
      )}
      <Header pageTitle={lang.GLOBAL_AUDITLOG} />

      <HelpdeskFilterBar
        onSearch={loadAuditLogs}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          eid: true,
          beginDateTime: true,
          endDateTime: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Gid</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID_MIN}</Table.Th>
              <Table.Th>{lang.GLOBAL_SOURCE}</Table.Th>
              <Table.Th>SeqId</Table.Th>
              <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
              <Table.Th>{lang.HELPDESK_AGREEMENT_ENTID}</Table.Th>
              <Table.Th>Uid</Table.Th>
              <Table.Th>{lang.GLOBAL_PERIOD}</Table.Th>
              <Table.Th>{lang.GLOBAL_CODE}</Table.Th>
              <Table.Th>{lang.GLOBAL_TYPE}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!list.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  list?.map((audit, index) => (
                    <Table.Tr key={index} onClick={() => showAuditLogDetails(index)}>
                      <Table.Td>{audit.gid}</Table.Td>
                      <Table.Td>{audit.eid}</Table.Td>
                      <Table.Td>{audit.source}</Table.Td>
                      <Table.Td>{audit.seqid}</Table.Td>
                      <Table.Td>{formatDateTimeNSecLabel(audit.dateTime, region)}</Table.Td>
                      <Table.Td>{audit.entid}</Table.Td>
                      <Table.Td>{audit.uid}</Table.Td>
                      <Table.Td>{audit.period}</Table.Td>
                      <Table.Td>{audit.code}</Table.Td>
                      <Table.Td>{audit.atype}</Table.Td>
                    </Table.Tr>
                  ))
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          currentPage={currentPage}
        />
      </Group>
    </div>
  );
};

export default HelpdeskAuditLogs;
